import type { CustomThemeConfig } from '../types';

type ThemeIdentity = Pick<CustomThemeConfig, 'id' | 'layoutType'>;

/** Opt in only the built-in Crystal theme. Imported and future themes remain independent. */
export function usesCrystalCompactLayout(theme: ThemeIdentity | null | undefined): boolean {
    return theme?.id === 'default' && theme.layoutType === 'crystal';
}

/** Shared presentation authority: change spacing here, then run THEME-LAYOUT-001. */
export const crystalLayoutClasses = {
    "shopPage": {
        "compact": "min-h-screen bg-[#FAFAFC] pt-[calc(4.5rem+var(--promo-banner-height,0px))] pb-10 md:pb-24",
        "standard": "min-h-screen bg-[#FAFAFC] pt-24 lg:pt-32 pb-10 md:pb-24"
    },
    "shopHero": {
        "compact": "max-w-7xl mx-auto px-4 sm:px-6 pt-4 md:pt-5 pb-4 md:pb-5 relative z-10",
        "standard": "max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-12 md:pb-20 relative z-10"
    },
    "shopIntro": {
        "compact": "text-center mb-4 max-w-4xl mx-auto",
        "standard": "text-center mb-10 max-w-3xl mx-auto"
    },
    "shopBadge": {
        "compact": "inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-2",
        "standard": "inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-50 border border-blue-100 mb-8"
    },
    "catalogTitle": {
        "compact": "text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-2 uppercase tracking-tighter leading-[1.05] drop-shadow-sm",
        "standard": "text-4xl md:text-7xl font-black text-gray-900 mb-6 uppercase tracking-tighter leading-[1.05] drop-shadow-sm"
    },
    "catalogDescription": {
        "compact": "text-gray-500 font-medium text-sm md:text-base max-w-2xl mx-auto",
        "standard": "text-gray-500 font-medium text-lg md:text-xl max-w-2xl mx-auto"
    },
    "catalogSearch": {
        "compact": "relative max-w-2xl mx-auto mb-4",
        "standard": "relative max-w-2xl mx-auto mb-12"
    },
    "catalogInput": {
        "compact": "w-full bg-white border border-gray-200 rounded-2xl py-3 px-4 pl-12 font-medium text-base text-gray-900 focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all shadow-sm placeholder:text-gray-400 relative z-10",
        "standard": "w-full bg-white border border-gray-200 rounded-[2rem] py-5 md:py-6 px-6 pl-16 font-medium text-lg text-gray-900 focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all shadow-sm placeholder:text-gray-400 relative z-10"
    },
    "catalogSearchIcon": {
        "compact": "w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 z-20",
        "standard": "w-6 h-6 text-gray-400 absolute left-6 top-1/2 -translate-y-1/2 z-20"
    },
    "shopFilterTitle": {
        "compact": "text-xs font-black text-gray-400 mb-2 uppercase tracking-[0.25em]",
        "standard": "text-xs font-black text-gray-400 mb-6 uppercase tracking-[0.25em]"
    },
    "shopFilters": {
        "compact": "flex gap-2 overflow-x-auto pb-2 no-scrollbar max-w-full w-full justify-start md:flex-wrap md:justify-center",
        "standard": "flex gap-3 overflow-x-auto pb-4 no-scrollbar max-w-full px-4 w-full justify-start md:justify-center"
    },
    "shopFilterButton": {
        "compact": "px-4 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all whitespace-nowrap border ",
        "standard": "px-8 py-4 rounded-[1.25rem] font-black text-[10px] uppercase tracking-widest transition-all whitespace-nowrap border "
    },
    "inventoryContainer": {
        "compact": "max-w-7xl mx-auto px-4 sm:px-6 pt-5",
        "standard": "max-w-7xl mx-auto px-6 pt-16"
    },
    "inventoryHeader": {
        "compact": "flex justify-between items-center gap-3 mb-4",
        "standard": "flex justify-between items-center mb-12"
    },
    "inventoryTitle": {
        "compact": "text-lg md:text-2xl font-black text-gray-900 uppercase tracking-tighter",
        "standard": "text-2xl md:text-3xl font-black text-gray-900 uppercase tracking-tighter"
    },
    "sellPage": {
        "compact": "min-h-screen pt-[calc(4.5rem+var(--promo-banner-height,0px))] pb-6 px-4 bg-[#030305] relative overflow-hidden",
        "standard": "min-h-screen pt-24 pb-12 px-4 bg-[#030305] relative overflow-hidden"
    },
    "sellIntro": {
        "compact": "text-center mb-5",
        "standard": "text-center mb-12"
    },
    "sellBadge": {
        "compact": "inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-3 backdrop-blur-md",
        "standard": "inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8 backdrop-blur-md"
    },
    "sellTitle": {
        "compact": "text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3 uppercase tracking-tighter leading-[1.05] drop-shadow-2xl",
        "standard": "text-4xl md:text-7xl font-black text-white mb-6 uppercase tracking-tighter leading-[1.05] drop-shadow-2xl"
    },
    "sellDescription": {
        "compact": "text-slate-400 font-medium text-sm md:text-base max-w-2xl mx-auto",
        "standard": "text-slate-400 font-medium text-lg md:text-xl max-w-2xl mx-auto"
    },
    "sellProgress": {
        "compact": "mb-5 px-2",
        "standard": "mb-12 px-4"
    },
    "sellStep": {
        "compact": "w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center font-black transition-all duration-500 border ",
        "standard": "w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center font-black transition-all duration-500 border "
    },
    "sellForm": {
        "compact": "bg-white rounded-[3rem] shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-4 sm:p-6 md:p-8 border-4 border-slate-800 relative overflow-visible",
        "standard": "bg-white rounded-[3rem] shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-8 md:p-12 border-4 border-slate-800 relative overflow-visible"
    },
    "accessoriesPage": {
        "compact": "min-h-screen bg-[#FAFAFC] pt-[calc(4.5rem+var(--promo-banner-height,0px))] pb-6 md:pb-8",
        "standard": "min-h-screen bg-[#FAFAFC] pt-24 lg:pt-32 pb-10 md:pb-24"
    },
    "accessoriesHero": {
        "compact": "max-w-7xl mx-auto px-4 sm:px-6 pt-5 pb-5 relative z-10",
        "standard": "max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-12 md:pb-20 relative z-10"
    },
    "accessoriesIntro": {
        "compact": "text-center mb-4 max-w-3xl mx-auto",
        "standard": "text-center mb-10 max-w-3xl mx-auto"
    },
    "accessoriesBadge": {
        "compact": "inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-2",
        "standard": "inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-50 border border-blue-100 mb-8"
    },
    "accessoriesSearch": {
        "compact": "relative max-w-2xl mx-auto mb-4",
        "standard": "relative max-w-2xl mx-auto mb-16"
    },
    "accessoriesFilterTitle": {
        "compact": "text-xs font-black text-gray-400 mb-3 uppercase tracking-[0.25em]",
        "standard": "text-xs font-black text-gray-400 mb-6 uppercase tracking-[0.25em]"
    },
    "accessoriesCategory": {
        "compact": "rounded-2xl p-3 flex flex-col items-center justify-center min-h-24 cursor-pointer group border transition-all ",
        "standard": "rounded-[2rem] p-4 md:p-6 flex flex-col items-center justify-center h-32 md:h-40 cursor-pointer group border transition-all "
    },
    "accessoriesEmpty": {
        "compact": "py-10 flex flex-col items-center justify-center text-center bg-white border border-gray-150 rounded-[3rem] shadow-sm mt-8 relative overflow-hidden",
        "standard": "py-32 flex flex-col items-center justify-center text-center bg-white border border-gray-150 rounded-[3rem] shadow-sm mt-8 relative overflow-hidden"
    },
    "footerNewsletter": {
        "compact": "py-6 md:py-8 px-4 sm:px-6 border-b border-gray-100/50 bg-gradient-to-b from-[#FBFBFC]/50 to-white flex items-center justify-center",
        "standard": "py-12 md:py-20 px-6 border-b border-gray-100/50 bg-gradient-to-b from-[#FBFBFC]/50 to-white flex items-center justify-center"
    },
    "footerNewsletterGrid": {
        "compact": "max-w-6xl w-full grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-8 items-center",
        "standard": "max-w-6xl w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
    },
    "footerContainer": {
        "compact": "container mx-auto px-4 sm:px-6 py-6 md:py-8",
        "standard": "container mx-auto px-6 py-16 md:py-24"
    },
    "footerGrid": {
        "compact": "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6",
        "standard": "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-20"
    },
    "footerCard": {
        "compact": "group relative min-h-32 min-w-0 overflow-hidden rounded-[16px] border border-slate-800 bg-gradient-to-br from-slate-950 to-slate-800 even:from-blue-800 even:to-slate-950 p-5 text-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-950/15",
        "standard": "group relative min-h-32 overflow-hidden rounded-[16px] border border-slate-800 bg-gradient-to-br from-slate-950 to-slate-800 even:from-blue-800 even:to-slate-950 p-5 text-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-950/15"
    },
    "footerIcon": {
        "compact": "pointer-events-none absolute -bottom-8 -right-6 h-28 w-28 text-white/10 [&>svg]:h-full [&>svg]:w-full",
        "standard": "pointer-events-none absolute -bottom-8 -right-6 h-28 w-28 text-white/10 [&>svg]:h-full [&>svg]:w-full"
    },
    "footerText": {
        "compact": "relative z-10 min-w-0 break-words",
        "standard": "relative z-10 min-w-0 break-words"
    },
    "footerEmail": {
        "compact": "break-all text-sm font-black leading-tight tracking-tight text-white md:text-base",
        "standard": "break-all text-sm font-black leading-tight tracking-tight text-white md:text-base"
    },
    "footerBottom": {
        "compact": "pt-5 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-6",
        "standard": "pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-6"
    },
    "discoveryContainer": {
        "compact": "mt-6 pt-5 border-t border-gray-100",
        "standard": "mt-16 pt-12 border-t border-gray-100"
    },
    "discoveryTitle": {
        "compact": "text-xs font-black text-gray-600 uppercase tracking-[0.25em] mb-4 text-center flex items-center justify-center gap-3",
        "standard": "text-xs font-black text-gray-600 uppercase tracking-[0.25em] mb-8 text-center flex items-center justify-center gap-3"
    },
    "discoveryCard": {
        "compact": "p-4 bg-white border border-gray-100 rounded-[2rem] text-left hover:shadow-xl hover:shadow-blue-500/5 transition-all group relative overflow-hidden",
        "standard": "p-6 bg-white border border-gray-100 rounded-[2rem] text-left hover:shadow-xl hover:shadow-blue-500/5 transition-all group relative overflow-hidden"
    },
    "discoveryLocations": {
        "compact": "mt-5 flex flex-wrap justify-center gap-3 md:gap-5 text-[10px] font-bold text-gray-600 uppercase tracking-widest",
        "standard": "mt-12 flex flex-wrap justify-center gap-6 text-[10px] font-bold text-gray-600 uppercase tracking-widest"
    },
    "navigationDesktop": {
        "compact": "fixed top-[calc(0.5rem+var(--promo-banner-height,0px))] left-1/2 -translate-x-1/2 z-[4999] hidden lg:block transition-all duration-300 w-[95%] max-w-max",
        "standard": "fixed top-[calc(1.5rem+var(--promo-banner-height,0px))] left-1/2 -translate-x-1/2 z-[4999] hidden lg:block transition-all duration-300 w-[95%] max-w-max"
    },
    "navigationSurface": {
        "compact": "bg-[#111015]/90 backdrop-blur-3xl border border-white/[0.08] rounded-full py-1 px-2 xl:px-3 shadow-[0_6px_20px_rgba(0,0,0,0.2)] flex items-center lg:gap-3 xl:gap-5 2xl:gap-7",
        "standard": "bg-[#111015]/90 backdrop-blur-3xl border border-white/[0.08] rounded-full py-1.5 px-2 xl:py-2 xl:px-3.5 shadow-[0_25px_60px_rgba(0,0,0,0.85)] flex items-center lg:gap-3 xl:gap-5 2xl:gap-7"
    },
    "navigationLink": {
        "compact": "lg:px-2.5 lg:py-2 xl:px-3 xl:py-2 rounded-full text-[9px] lg:text-[10px] xl:text-[11px] font-black uppercase tracking-[0.1em] xl:tracking-[0.14em] transition-all duration-300 block ",
        "standard": "lg:px-2.5 lg:py-2 xl:px-4 xl:py-2.5 rounded-full text-[9px] lg:text-[10px] xl:text-[11px] font-black uppercase tracking-[0.1em] xl:tracking-[0.14em] transition-all duration-300 block "
    },
    "navigationMobile": {
        "compact": "absolute top-[calc(0.5rem+var(--promo-banner-height,0px))] left-4 right-4 z-[999] lg:hidden bg-[#111015]/80 backdrop-blur-2xl border border-white/[0.08] px-4.5 py-2 rounded-full shadow-[0_6px_20px_rgba(0,0,0,0.2)] flex items-center justify-between transition-all duration-300",
        "standard": "absolute top-[calc(1rem+var(--promo-banner-height,0px))] left-4 right-4 z-[999] lg:hidden bg-[#111015]/80 backdrop-blur-2xl border border-white/[0.08] px-4.5 py-2 rounded-full shadow-[0_15px_35px_rgba(0,0,0,0.6)] flex items-center justify-between transition-all duration-300"
    },
    "crystalPage": {
        "compact": "min-h-screen bg-[#F0F4FA] text-slate-900 font-sans selection:bg-blue-600 selection:text-white pb-6 overflow-hidden relative",
        "standard": "min-h-screen bg-[#F0F4FA] text-slate-900 font-sans selection:bg-blue-600 selection:text-white pb-32 overflow-hidden relative"
    },
    "crystalDiscovery": {
        "compact": "max-w-7xl mx-auto px-6 pb-0",
        "standard": "max-w-7xl mx-auto px-6 pb-20"
    }
} as const;

export type ThemeLayoutRole = keyof typeof crystalLayoutClasses;

export function getThemeLayoutClass(theme: ThemeIdentity | null | undefined, role: ThemeLayoutRole): string {
    return crystalLayoutClasses[role][usesCrystalCompactLayout(theme) ? 'compact' : 'standard'];
}
